import { useState } from "react";

export default function SignupSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    setStatus("loading");

    const body = new URLSearchParams();
    body.append("email", email);

    try {
      const res = await fetch("https://readdy.ai/api/form/d7akq9hnauq9gnuqnko0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setMessage("You&apos;re on the list! We&apos;ll notify you at launch.");
        setEmail("");
      } else {
        throw new Error("Submission failed");
      }
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="signup" className="relative py-28 px-4" style={{ background: "linear-gradient(180deg, #0d1117 0%, #080c14 100%)" }}>
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10" style={{ background: "radial-gradient(circle, rgba(100,200,255,0.4) 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto text-center">
        {/* Label */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-px w-12 bg-cyan-500/40" />
          <span className="text-cyan-400 text-xs font-mono tracking-widest uppercase">// Stay Updated</span>
          <div className="h-px w-12 bg-cyan-500/40" />
        </div>

        <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4">
          Be the <span className="text-cyan-400">First</span> to Know
        </h2>
        <p className="text-gray-400 text-base md:text-lg mb-10 leading-relaxed">
          Sign up for early access and exclusive launch offers. No spam — just the good stuff.
        </p>

        {/* Form */}
        <form
          data-readdy-form
          onSubmit={handleSubmit}
          className="relative rounded-2xl border border-white/8 p-8 md:p-10"
          style={{ background: "rgba(255,255,255,0.03)", backdropFilter: "blur(12px)" }}
        >
          {status === "success" ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-cyan-500/15 border border-cyan-500/30">
                <i className="ri-check-line text-cyan-400 text-2xl" />
              </div>
              <p className="text-white font-semibold text-lg">You&apos;re on the list!</p>
              <p className="text-gray-400 text-sm">We&apos;ll notify you the moment we launch.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setStatus("idle"); setMessage(""); }}
                  placeholder="Enter your email address"
                  className="flex-1 h-13 px-5 rounded-lg text-sm text-white placeholder-gray-500 outline-none transition-all duration-300 border border-white/10 focus:border-cyan-500/50"
                  style={{ background: "rgba(255,255,255,0.05)", height: "52px" }}
                  required
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="whitespace-nowrap h-13 px-7 rounded-lg font-bold text-sm tracking-wider text-white transition-all duration-300 hover:brightness-110 active:scale-95 disabled:opacity-60 cursor-pointer flex items-center gap-2"
                  style={{ height: "52px", background: "linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)" }}
                >
                  {status === "loading" ? (
                    <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                  ) : (
                    <>Notify Me <i className="ri-arrow-right-line" /></>
                  )}
                </button>
              </div>

              {status === "error" && (
                <p className="mt-3 text-red-400 text-xs text-left">{message}</p>
              )}

              <p className="mt-4 text-gray-600 text-xs">
                <i className="ri-lock-line mr-1" />
                We respect your privacy. Unsubscribe anytime.
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
}
